import {
  cleanValues,
  emptyProgress,
  getPack,
  nextStep,
  validateAll,
  type AdoptSignatureRequest,
  type AdoptedSignature,
  type CodeSent,
  type FormDoc,
  type LetterDoc,
  type LinkInfo,
  type LinkState,
  type SaveValuesResult,
  type SessionView,
  type SignerProgress,
  type UploadDoc,
  type Values,
  type VerifyResult,
} from "@gsignatures/core";
import { FAKE_COMPLETE, FAKE_FILES, FAKE_PACK, FAKE_SENDER, FAKE_SIGNER, fakeConsent, fakeForm, fakeLetter } from "./fake-data";
import { ApiFailure, type PreviewResult, type SignerApi } from "./types";

export type FakeStep = "consent" | "offer" | "tax" | "form" | "upload" | "review" | "sign" | "done";
export const FAKE_STEPS: readonly FakeStep[] = ["consent", "offer", "tax", "form", "upload", "review", "sign", "done"];

/** The demo's ID proof (the sender asks for one or two pictures). */
const fakeUpload = (): UploadDoc => ({
  id: "doc-idproof", kind: "upload", title: "ID proof", required: true, maxFiles: 2, files: [],
  help: "A clear photo of your PAN card, or of your passport's photo page. If you use Aadhaar, only a masked Aadhaar (just the last 4 digits showing).",
});
/** A sample picture for demos that start past the upload step. */
const SAMPLE_PICTURE = "/demo/id-card.jpg";

export interface FakeOptions {
  /** Link state to start in (`?state=`). */
  state?: LinkState;
  /** Start already verified at this step, with sample answers (`?step=`), for demos and screenshots. */
  step?: FakeStep;
  /** Pretend network time per call. 0 in tests. */
  latencyMs?: number;
  /** "Today", injected so tests agree with the rules engine. */
  now?: () => Date;
}

const iso = (d: Date) => d.toISOString();
const MIN = 60_000;

/** A small stable hash, so the fake preview fingerprint changes when what's signed changes. */
function stableHash(s: string): string {
  let a = 0x811c9dc5;
  let b = 0x01000193;
  for (let i = 0; i < s.length; i++) {
    a = Math.imul(a ^ s.charCodeAt(i), 0x01000193) >>> 0;
    b = Math.imul(b + s.charCodeAt(i), 0x5bd1e995) >>> 0;
  }
  return (a.toString(16).padStart(8, "0") + b.toString(16).padStart(8, "0")).repeat(4);
}

/**
 * The signer API, answered in the browser. Same rules as the server: core's validation,
 * core's `nextStep`, the pack's policy. Any 6 digits verify; `000000` is a wrong code,
 * and five wrong codes pause the link.
 */
export class FakeSignerApi implements SignerApi {
  readonly fake = true;
  private linkState: LinkState;
  private verified = false;
  private tries = 5;
  private progress: SignerProgress = emptyProgress();
  private letter: LetterDoc = fakeLetter();
  private form: FormDoc = fakeForm();
  private upload: UploadDoc = fakeUpload();
  /** Picture id → what to show (a data URL the signer added, or the sample). */
  private pictures = new Map<string, string>();
  private signature: AdoptedSignature | undefined;
  private completedAt: string | undefined;
  private readonly latency: number;
  private readonly now: () => Date;
  private readonly sentAt: Date;
  /** Calls made, in order — tests read this. */
  readonly calls: string[] = [];

  constructor(opts: FakeOptions = {}) {
    this.latency = opts.latencyMs ?? 0;
    this.now = opts.now ?? (() => new Date());
    this.sentAt = new Date(this.now().getTime() - 4 * MIN);
    this.linkState = opts.state ?? "ready";
    if (opts.state === "completed") this.seed("done");
    if (opts.step && this.linkState === "ready") {
      this.seed(opts.step);
      this.verified = true;
    }
  }

  /* ---------------- setup ---------------- */

  private seed(step: FakeStep): void {
    const t = iso(this.now());
    const at = FAKE_STEPS.indexOf(step);
    this.progress.codeVerifiedAt = t;
    if (at >= 1) this.progress.consentedAt = t;
    if (at >= 2) this.progress.accepted[this.letter.id] = t;
    if (at >= 3) this.choose(false);
    if (at >= 4) {
      this.form.values = { ...FAKE_COMPLETE };
      this.revalidate();
    }
    if (at >= 5) {
      this.upload.files = [{ id: "file-sample", name: "pan-card.jpg", sha256: stableHash("sample"), bytes: 6022, at: t }];
      this.pictures.set("file-sample", SAMPLE_PICTURE);
      (this.progress.uploaded ??= {})[this.upload.id] = t;
    }
    if (at >= 6) this.progress.reviewedAt = t;
    if (at >= 7) {
      this.signature = { kind: "type", text: FAKE_SIGNER.name, font: "homemade-apple" };
      this.progress.signatureAdoptedAt = t;
      this.progress.signed[this.letter.id] = t;
      this.progress.signed[this.form.id] = t;
      this.completedAt = t;
      this.linkState = "completed";
    }
  }

  private choose(usPerson: boolean): void {
    if (usPerson) return;
    const pack = getPack(FAKE_PACK);
    this.progress.formChosen = FAKE_PACK;
    this.form.pack = FAKE_PACK;
    this.form.policy = pack.defaultPolicy;
    this.revalidate();
  }

  private revalidate(): void {
    if (!this.form.pack) return;
    const pack = getPack(this.form.pack);
    const ctx = { now: this.now(), ...(this.form.policy ? { policy: this.form.policy } : {}) };
    const validation = validateAll(pack.fields, this.form.values, ctx);
    this.form.validation = validation;
    this.progress.formsComplete[this.form.id] = validation.errors === 0;
  }

  /* ---------------- plumbing ---------------- */

  private async tick(name: string): Promise<void> {
    this.calls.push(name);
    if (this.latency > 0) await new Promise((r) => setTimeout(r, this.latency));
  }

  private requireOpen(): void {
    if (this.linkState !== "ready") throw new ApiFailure("invalid_link", "This link isn't active.", 404);
  }

  /** A finished or paper envelope still lets the signer prove who they are, to download copies. */
  private requireReachable(): void {
    if (!["ready", "completed", "paper"].includes(this.linkState)) throw new ApiFailure("invalid_link", "This link isn't active.", 404);
  }

  private requireSession(): void {
    this.requireOpen();
    if (!this.verified) throw new ApiFailure("session_required", "Confirm it's you first.", 401);
  }

  private step() {
    return nextStep(this.progress, [this.letter, this.form, this.upload]);
  }

  private view(): SessionView {
    const n = this.now();
    return structuredClone({
      envelope: { id: "env_01JB8Q4MZK2", status: this.linkState === "completed" ? "completed" : "sent", sentAt: iso(this.sentAt), expiresAt: iso(new Date(this.sentAt.getTime() + 14 * 24 * 60 * MIN)) },
      sender: FAKE_SENDER,
      signer: FAKE_SIGNER,
      step: this.step(),
      progress: this.progress,
      consent: fakeConsent(),
      documents: [this.letter, this.form, this.upload],
      ...(this.signature ? { signature: this.signature } : {}),
      sessionExpiresAt: iso(new Date(n.getTime() + 30 * MIN)),
    });
  }

  private linkInfo(): LinkInfo {
    return {
      state: this.linkState,
      sender: { ...FAKE_SENDER },
      signer: { firstName: FAKE_SIGNER.firstName, maskedEmail: FAKE_SIGNER.maskedEmail },
      expiresAt: iso(new Date(this.sentAt.getTime() + 14 * 24 * 60 * MIN)),
      session: this.verified,
      ...(this.completedAt ? { completedAt: this.completedAt } : {}),
    };
  }

  private previewSha(documentId: string): string {
    const sig = JSON.stringify(this.signature ?? null);
    const vals = documentId === this.form.id ? JSON.stringify(this.form.values) : "";
    return stableHash(`${documentId}|${sig}|${vals}`);
  }

  /* ---------------- the API ---------------- */

  async link(): Promise<LinkInfo> {
    await this.tick("link");
    return this.linkInfo();
  }

  async sendCode(): Promise<CodeSent> {
    await this.tick("sendCode");
    this.requireReachable();
    // Code timing runs on the wall clock, as a real server's would; `now` only drives the rules.
    const n = Date.now();
    return { sentTo: FAKE_SIGNER.maskedEmail, expiresAt: iso(new Date(n + 10 * MIN)), resendAt: iso(new Date(n + 45_000)) };
  }

  async verify(code: string): Promise<VerifyResult> {
    await this.tick("verify");
    if (this.linkState === "paused") return { ok: false, reason: "paused" };
    this.requireReachable();
    if (!/^\d{6}$/.test(code)) throw new ApiFailure("bad_request", "The code has 6 digits.", 400);
    if (code === "000000") {
      this.tries -= 1;
      if (this.tries <= 0) {
        this.linkState = "paused";
        return { ok: false, reason: "paused" };
      }
      return { ok: false, reason: "wrong", triesLeft: this.tries };
    }
    this.verified = true;
    this.progress.codeVerifiedAt ??= iso(this.now());
    return { ok: true };
  }

  async session(): Promise<SessionView> {
    await this.tick("session");
    this.requireReachable();
    if (!this.verified) throw new ApiFailure("session_required", "Confirm it's you first.", 401);
    return this.view();
  }

  async consent(_version: string): Promise<SessionView> {
    await this.tick("consent");
    this.requireSession();
    this.progress.consentedAt ??= iso(this.now());
    return this.view();
  }

  async viewed(_id: string, _sha: string): Promise<void> {
    await this.tick("viewed");
    this.requireSession();
  }

  fileUrl(id: string): string {
    return FAKE_FILES[id] ?? "/demo/missing.pdf";
  }

  async accept(id: string): Promise<SessionView> {
    await this.tick("accept");
    this.requireSession();
    if (id !== this.letter.id) throw new ApiFailure("not_found", "No such document.", 404);
    if (!this.progress.consentedAt) throw new ApiFailure("wrong_step", "Consent first.", 409);
    this.progress.accepted[id] ??= iso(this.now());
    return this.view();
  }

  async chooseForm(id: string, usPerson: boolean): Promise<SessionView> {
    await this.tick("chooseForm");
    this.requireSession();
    if (id !== this.form.id) throw new ApiFailure("not_found", "No such document.", 404);
    if (usPerson) throw new ApiFailure("bad_request", "Form W-9 isn't available yet.", 400);
    this.choose(false);
    return this.view();
  }

  async saveValues(id: string, values: Values): Promise<SaveValuesResult> {
    await this.tick("saveValues");
    this.requireSession();
    if (id !== this.form.id || !this.form.pack) throw new ApiFailure("wrong_step", "Choose the form first.", 409);
    if (this.progress.signed[id]) throw new ApiFailure("conflict", "This form is already signed.", 409);
    const pack = getPack(this.form.pack);
    this.form.values = cleanValues(pack.fields, values);
    this.revalidate();
    const savedAt = iso(this.now());
    this.form.savedAt = savedAt;
    return structuredClone({ values: this.form.values, validation: this.form.validation!, savedAt });
  }

  async review(): Promise<SessionView> {
    await this.tick("review");
    this.requireSession();
    if (this.step().step !== "review" && !this.progress.reviewedAt) throw new ApiFailure("wrong_step", "Finish the form first.", 409);
    this.progress.reviewedAt ??= iso(this.now());
    return this.view();
  }

  async adoptSignature(req: AdoptSignatureRequest): Promise<SessionView> {
    await this.tick("adoptSignature");
    this.requireSession();
    this.signature = req.kind === "type" ? { kind: "type", text: req.text, font: req.font } : { kind: "image", url: req.dataUrl };
    this.progress.signatureAdoptedAt = iso(this.now());
    return this.view();
  }

  async preview(id: string): Promise<PreviewResult> {
    await this.tick("preview");
    this.requireSession();
    return { sha256: this.previewSha(id), blob: null };
  }

  async certificationShown(_id: string): Promise<void> {
    await this.tick("certificationShown");
    this.requireSession();
  }

  async sign(id: string, previewSha256: string): Promise<SessionView> {
    await this.tick("sign");
    this.requireSession();
    const step = this.step();
    if (step.step !== "sign" || step.documentId !== id) throw new ApiFailure("wrong_step", "That isn't the next thing to sign.", 409);
    if (!this.signature) throw new ApiFailure("bad_request", "Adopt a signature first.", 400);
    if (previewSha256 !== this.previewSha(id)) throw new ApiFailure("preview_mismatch", "The document changed.", 409);
    if (id === this.form.id && (this.form.validation?.errors ?? 1) > 0) throw new ApiFailure("invalid_values", "Some answers need a fix.", 422, this.form.validation);
    const t = iso(this.now());
    this.progress.signed[id] = t;
    if (this.step().step === "done") {
      this.completedAt = t;
      this.linkState = "completed";
    }
    return this.view();
  }

  downloadUrl(id: string): string {
    return this.fileUrl(id);
  }

  async decline(_reason?: string): Promise<LinkInfo> {
    await this.tick("decline");
    this.requireSession();
    this.linkState = "declined";
    return this.linkInfo();
  }

  async paper(): Promise<LinkInfo> {
    await this.tick("paper");
    this.requireSession();
    this.linkState = "paper";
    return this.linkInfo();
  }

  /* ---------------- pictures (an ID proof) ---------------- */

  async addPicture(documentId: string, dataUrl: string, name?: string): Promise<SessionView> {
    await this.tick("addPicture");
    this.requireSession();
    if (documentId !== this.upload.id) throw new ApiFailure("not_found", "No such document.", 404);
    if (Object.keys(this.progress.signed).length) throw new ApiFailure("wrong_step", "Signing has started — the pictures can't change any more.", 409);
    if (!/^data:image\/(jpeg|png);base64,/.test(dataUrl)) throw new ApiFailure("bad_request", "Send a JPEG or PNG picture.", 400);
    if (this.upload.files.length >= this.upload.maxFiles) throw new ApiFailure("conflict", "At most 2 pictures — remove one first.", 409);
    const t = iso(this.now());
    const id = `file-${this.upload.files.length + 1}-${stableHash(dataUrl).slice(0, 6)}`;
    this.upload.files.push({ id, name: name ?? "picture.jpg", sha256: stableHash(dataUrl), bytes: Math.floor((dataUrl.length * 3) / 4), at: t });
    this.pictures.set(id, dataUrl);
    (this.progress.uploaded ??= {})[this.upload.id] = t;
    delete this.progress.reviewedAt;
    return this.view();
  }

  async removePicture(documentId: string, fileId: string): Promise<SessionView> {
    await this.tick("removePicture");
    this.requireSession();
    if (documentId !== this.upload.id) throw new ApiFailure("not_found", "No such document.", 404);
    if (Object.keys(this.progress.signed).length) throw new ApiFailure("wrong_step", "Signing has started — the pictures can't change any more.", 409);
    this.upload.files = this.upload.files.filter((f) => f.id !== fileId);
    this.pictures.delete(fileId);
    if (!this.upload.files.length) delete this.progress.uploaded?.[this.upload.id];
    delete this.progress.reviewedAt;
    return this.view();
  }

  pictureUrl(_documentId: string, fileId: string): string {
    return this.pictures.get(fileId) ?? SAMPLE_PICTURE;
  }
}
